import { useEffect, useState } from "react";
import { api, apiErr } from "@/lib/api";
import Layout, { ROLE_LABEL } from "@/components/Layout";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Page, Card, TH, TD } from "@/components/polaris";
import { Pill } from "@/components/AnswerBits";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { UserPlus, KeyRound, Trash2 } from "lucide-react";
import { toast } from "sonner";

const ROLES = ["admin", "reviewer", "loan_officer"];

export default function Users() {
  const { user: me } = useAuth();
  const isSuper = me?.role === "super_admin";
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = () => {
    setLoading(true);
    api.get("/users").then((r) => setUsers(r.data)).catch((e) => toast.error(apiErr(e))).finally(() => setLoading(false));
  };
  useEffect(load, []);

  const setRole = async (id, role) => {
    try { await api.patch(`/users/${id}/role`, { role }); toast.success("Role updated"); load(); }
    catch (e) { toast.error(apiErr(e)); }
  };
  const remove = async (id) => {
    try { await api.delete(`/users/${id}`); toast.success("User deleted"); load(); }
    catch (e) { toast.error(apiErr(e)); }
  };

  return (
    <Layout>
      <Page title="Users" width="max-w-5xl"
        subtitle={isSuper ? "Create accounts, assign roles, and reset passwords." : "Reset passwords and remove users. Only the Super Admin can add users or change roles."}
        actions={isSuper && <CreateUserDialog onDone={load} />}>
        <Card>
          {loading && users.length === 0 && <div className="p-6 text-sm text-[#6D7175]">Loading…</div>}
          <div className="overflow-x-auto">
            <table className="w-full min-w-[640px] border-collapse">
              <thead>
                <tr><th className={TH}>Name</th><th className={TH}>Role</th><th className={`${TH} text-right`}><span className="sr-only">Actions</span></th></tr>
              </thead>
              <tbody>
                {users.map((u) => (
                  <tr key={u.id} className="hover:bg-[#FAFAFB] transition-colors" data-testid={`user-${u.id}`}>
                    <td className={TD}>
                      <div className="flex items-center gap-3">
                        <span className="h-8 w-8 shrink-0 rounded-full bg-[#E3EEF7] text-[#05304F] text-xs font-semibold flex items-center justify-center">
                          {(u.name || u.email || "?").split(/\s+/).map((x) => x[0]).slice(0, 2).join("").toUpperCase()}
                        </span>
                        <div className="min-w-0">
                          <div className="font-medium text-[#202223] flex items-center gap-2">
                            {u.name}
                            {u.id === me?.id && <Pill tone="highlight">You</Pill>}
                          </div>
                          <div className="text-xs text-[#6D7175]">{u.email}</div>
                        </div>
                      </div>
                    </td>
                    <td className={TD}>
                      {u.role === "super_admin" || !isSuper ? (
                        <Pill tone={u.role === "super_admin" ? "highlight" : "neutral"} data-testid={`role-label-${u.id}`}>{ROLE_LABEL[u.role] || u.role}</Pill>
                      ) : (
                      <Select value={u.role} onValueChange={(r) => setRole(u.id, r)}>
                        <SelectTrigger className="h-8 w-[160px] text-[13px] rounded-lg bg-white border-[#C9CCCF]" data-testid={`role-select-${u.id}`}>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {ROLES.map((r) => <SelectItem key={r} value={r}>{ROLE_LABEL[r]}</SelectItem>)}
                        </SelectContent>
                      </Select>
                      )}
                    </td>
                    <td className={`${TD} text-right whitespace-nowrap`}>
                      {(u.role !== "super_admin" || isSuper) && <ResetPasswordDialog userId={u.id} email={u.email} />}
                      {u.id !== me?.id && u.role !== "super_admin" && (
                        <AlertDialog>
                          <AlertDialogTrigger asChild>
                            <Button variant="ghost" size="sm" className="h-8 gap-1 text-xs text-[#7A1408] hover:bg-[#FDE8E8] hover:text-[#7A1408] rounded-lg" data-testid={`delete-user-${u.id}`}>
                              <Trash2 className="h-3.5 w-3.5" /> Delete
                            </Button>
                          </AlertDialogTrigger>
                          <AlertDialogContent>
                            <AlertDialogHeader>
                              <AlertDialogTitle>Delete {u.email}?</AlertDialogTitle>
                              <AlertDialogDescription>This permanently removes the account. This cannot be undone.</AlertDialogDescription>
                            </AlertDialogHeader>
                            <AlertDialogFooter>
                              <AlertDialogCancel>Cancel</AlertDialogCancel>
                              <AlertDialogAction onClick={() => remove(u.id)} data-testid={`confirm-delete-${u.id}`}
                                className="bg-[#D72C0D] text-white hover:bg-[#BC2200]">Delete</AlertDialogAction>
                            </AlertDialogFooter>
                          </AlertDialogContent>
                        </AlertDialog>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      </Page>
    </Layout>
  );
}

function CreateUserDialog({ onDone }) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", password: "", role: "loan_officer" });
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async () => {
    setBusy(true);
    try {
      await api.post("/users", form);
      toast.success("User created");
      setOpen(false);
      setForm({ name: "", email: "", password: "", role: "loan_officer" });
      onDone();
    } catch (e) { toast.error(apiErr(e)); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button className="gap-2" data-testid="create-user-button"><UserPlus className="h-4 w-4" /> Add user</Button>
      </DialogTrigger>
      <DialogContent className="max-w-md">
        <DialogHeader><DialogTitle>Create user account</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div className="space-y-1.5"><Label>Full name</Label>
            <Input value={form.name} onChange={(e) => set("name", e.target.value)} data-testid="new-user-name" /></div>
          <div className="space-y-1.5"><Label>Email</Label>
            <Input type="email" value={form.email} onChange={(e) => set("email", e.target.value)} data-testid="new-user-email" /></div>
          <div className="space-y-1.5"><Label>Temporary password</Label>
            <Input type="password" value={form.password} onChange={(e) => set("password", e.target.value)} data-testid="new-user-password" placeholder="min 6 characters" /></div>
          <div className="space-y-1.5"><Label>Role</Label>
            <Select value={form.role} onValueChange={(r) => set("role", r)}>
              <SelectTrigger data-testid="new-user-role"><SelectValue /></SelectTrigger>
              <SelectContent>{ROLES.map((r) => <SelectItem key={r} value={r}>{ROLE_LABEL[r]}</SelectItem>)}</SelectContent>
            </Select></div>
        </div>
        <DialogFooter><Button onClick={submit} disabled={busy} data-testid="submit-new-user">{busy ? "Creating…" : "Create user"}</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ResetPasswordDialog({ userId, email }) {
  const [open, setOpen] = useState(false);
  const [pw, setPw] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    setBusy(true);
    try { await api.patch(`/users/${userId}/password`, { password: pw }); toast.success("Password reset"); setOpen(false); setPw(""); }
    catch (e) { toast.error(apiErr(e)); }
    finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="ghost" size="sm" className="h-8 gap-1 text-xs text-[#005180] hover:bg-[#F0F7FF] rounded-lg" data-testid={`reset-pw-${userId}`}><KeyRound className="h-3.5 w-3.5" /> Reset password</Button>
      </DialogTrigger>
      <DialogContent className="max-w-sm">
        <DialogHeader><DialogTitle>Reset password</DialogTitle></DialogHeader>
        <p className="text-sm text-muted-foreground -mt-2">Set a new password for <span className="font-medium text-[#202223]">{email}</span>.</p>
        <div className="space-y-1.5"><Label>New password</Label>
          <Input type="password" value={pw} onChange={(e) => setPw(e.target.value)} data-testid="reset-pw-input" placeholder="min 6 characters" /></div>
        <DialogFooter><Button onClick={submit} disabled={busy || pw.length < 6} data-testid="submit-reset-pw">Reset password</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
